import React, { useEffect, useState, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useCart } from "../context/CartContext";

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();

  const API_BASE = import.meta.env.VITE_API_BASE_URL;

  const [product, setProduct] = useState(null);
  const [activeImage, setActiveImage] = useState(null);
  const [activeTab, setActiveTab] = useState("description");
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVariant, setSelectedVariant] = useState(null);

  // ─────────────────────────────
  // Load product + related
  // ─────────────────────────────
  useEffect(() => {
    let isMounted = true;

    async function loadProduct() {
      try {
        setLoading(true);
        const res = await axios.get(`${API_BASE}/api/products/${id}`);
        if (!isMounted) return;

        const p = res.data;
        setProduct(p);
        setActiveImage(p.images?.[0] || null);

        if (Array.isArray(p.variants) && p.variants.length > 0) {
          setSelectedVariant(p.variants[0]);
        } else {
          setSelectedVariant(null);
        }
      } catch (err) {
        console.error("Error loading product", err);
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    async function loadRelated() {
      try {
        const res = await axios.get(`${API_BASE}/api/products/related/${id}`);
        if (!isMounted) return;
        setRelatedProducts(res.data || []);
      } catch (err) {
        console.warn("Related products error (safe to ignore):", err.message);
      }
    }

    loadProduct();
    loadRelated();

    return () => {
      isMounted = false;
    };
  }, [id, API_BASE]);

  // ─────────────────────────────
  // Price & stock logic
  // ─────────────────────────────
  const effectivePrice = useMemo(() => {
    if (!product) return null;
    if (selectedVariant && typeof selectedVariant.price === "number") {
      return selectedVariant.price;
    }
    return Number(product.price || 0);
  }, [product, selectedVariant]);

  const { inStock, stockLabel, stockColor } = useMemo(() => {
    if (!product) {
      return {
        inStock: false,
        stockLabel: "Checking stock…",
        stockColor: "text-gray-500 bg-gray-100",
      };
    }

    // If variant selected and it has stock, use that
    if (
      selectedVariant &&
      typeof selectedVariant.stock === "number"
    ) {
      const s = selectedVariant.stock;
      if (s <= 0) {
        return {
          inStock: false,
          stockLabel: `Out of stock (${selectedVariant.name})`,
          stockColor: "text-red-600 bg-red-50",
        };
      }
      if (s <= 5) {
        return {
          inStock: true,
          stockLabel: `Low stock (${s})`,
          stockColor: "text-amber-700 bg-amber-50",
        };
      }
      return {
        inStock: true,
        stockLabel: `In stock (${s})`,
        stockColor: "text-emerald-700 bg-emerald-50",
      };
    }

    // Fallback: base product stock
    if (typeof product.stock === "number") {
      const s = product.stock;
      if (s <= 0) {
        return {
          inStock: false,
          stockLabel: "Out of stock",
          stockColor: "text-red-600 bg-red-50",
        };
      }
      if (s <= 5) {
        return {
          inStock: true,
          stockLabel: `Low stock (${s})`,
          stockColor: "text-amber-700 bg-amber-50",
        };
      }
      return {
        inStock: true,
        stockLabel: `In stock (${s})`,
        stockColor: "text-emerald-700 bg-emerald-50",
      };
    }

    // If no stock info, assume in stock
    return {
      inStock: true,
      stockLabel: "In stock",
      stockColor: "text-emerald-700 bg-emerald-50",
    };
  }, [product, selectedVariant]);

  // ─────────────────────────────
  // Cart handlers
  // ─────────────────────────────
  const handleAddToCart = () => {
    if (!product) return;

    if (selectedVariant) {
      addToCart(product, {
        variantName: selectedVariant.name,
        price: selectedVariant.price || product.price,
        sku: selectedVariant.sku,
      });
    } else {
      addToCart(product);
    }
  };

  const handleBuyNow = () => {
    if (!product) return;

    if (selectedVariant) {
      addToCart(product, {
        variantName: selectedVariant.name,
        price: selectedVariant.price || product.price,
        sku: selectedVariant.sku,
      });
    } else {
      addToCart(product);
    }

    navigate("/checkout");
  };

  // ─────────────────────────────
  // Loading / not found
  // ─────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 text-gray-500">
        Loading product…
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 text-gray-500">
        Product not found.
      </div>
    );
  }

  // ─────────────────────────────
  // UI
  // ─────────────────────────────
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-6xl mx-auto px-4 py-8 lg:py-12">
        {/* Breadcrumb */}
        <nav className="text-xs text-gray-500 mb-6 flex flex-wrap gap-1">
          <Link to="/" className="hover:text-black">
            Home
          </Link>
          <span>/</span>
          <Link to="/products" className="hover:text-black">
            Products
          </Link>
          {product.category && (
            <>
              <span>/</span>
              <span>{product.category}</span>
            </>
          )}
        </nav>

        {/* Main layout: sticky gallery + details */}
        <div className="grid lg:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)] gap-10 lg:gap-14 items-start">
          {/* LEFT ─ Sticky gallery */}
          <div className="lg:sticky lg:top-24 space-y-4">
            <div className="relative bg-white rounded-3xl overflow-hidden shadow-sm border border-gray-100">
              <div className="aspect-[4/3]">
                {activeImage ? (
                  <img
                    src={activeImage}
                    alt={product.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-300 text-sm">
                    No image available
                  </div>
                )}
              </div>
            </div>

            {product.images && product.images.length > 1 && (
              <div className="flex gap-3 overflow-x-auto pb-1">
                {product.images.map((img, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => setActiveImage(img)}
                    className={`w-20 h-20 rounded-2xl overflow-hidden border-2 flex-shrink-0 transition ${
                      img === activeImage
                        ? "border-black"
                        : "border-transparent hover:border-gray-300"
                    }`}
                  >
                    <img
                      src={img}
                      alt={`${product.name} ${idx + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* RIGHT ─ Details */}
          <div className="space-y-8">
            {/* Title / subtitle / rating */}
            <div>
              <p className="uppercase tracking-[0.2em] text-xs text-gray-500 mb-2">
                RelaxB Collection
              </p>
              <h1 className="text-3xl md:text-4xl font-semibold text-gray-900 mb-2 leading-tight">
                {product.name}
              </h1>
              {product.category && (
                <p className="text-sm text-gray-500 mb-1">
                  {product.category}
                </p>
              )}
              <div className="flex items-center gap-2 text-xs text-gray-500 mt-2">
                <span className="text-yellow-500">★★★★★</span>
                <span>Premium comfort experience</span>
              </div>
            </div>

            {/* Price + stock pill */}
            <div className="flex items-center gap-4">
              {effectivePrice !== null && (
                <div className="text-3xl font-semibold text-gray-900">
                  ${effectivePrice.toLocaleString()}
                </div>
              )}
              <span
                className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${stockColor}`}
              >
                {stockLabel}
              </span>
            </div>

            {/* Variant selector */}
            {Array.isArray(product.variants) &&
              product.variants.length > 0 && (
                <div>
                  <div className="text-xs font-medium text-gray-700 mb-2">
                    Choose configuration
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {product.variants.map((v) => (
                      <button
                        key={v._id || v.name}
                        type="button"
                        onClick={() => setSelectedVariant(v)}
                        className={`px-3 py-2 rounded-full border text-xs md:text-sm transition ${
                          selectedVariant === v
                            ? "border-black bg-black text-white"
                            : "border-gray-300 bg-white text-gray-800 hover:border-black/50"
                        }`}
                      >
                        {v.name}
                        {typeof v.price === "number" &&
                          v.price !== product.price && (
                            <span className="ml-1 text-[11px] opacity-80">
                              (${v.price.toLocaleString()})
                            </span>
                          )}
                      </button>
                    ))}
                  </div>
                </div>
              )}

            {/* Short description */}
            {product.description && (
              <p className="text-sm text-gray-600 leading-relaxed">
                {product.description}
              </p>
            )}

            {/* Primary actions */}
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                onClick={handleBuyNow}
                disabled={!inStock}
                className={`flex-1 inline-flex items-center justify-center rounded-full px-4 py-3 text-sm font-medium shadow-sm transition ${
                  inStock
                    ? "bg-black text-white hover:bg-gray-900"
                    : "bg-gray-300 text-gray-600 cursor-not-allowed"
                }`}
              >
                Buy now
              </button>
              <button
                type="button"
                onClick={handleAddToCart}
                disabled={!inStock}
                className={`flex-1 inline-flex items-center justify-center rounded-full px-4 py-3 text-sm font-medium border transition ${
                  inStock
                    ? "border-gray-300 text-gray-900 hover:border-black"
                    : "border-gray-200 text-gray-400 cursor-not-allowed"
                }`}
              >
                Add to cart
              </button>
            </div>

            {/* Trust badges */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs text-gray-600">
              <div className="flex items-center gap-2">
                <span className="text-lg">🚚</span>
                <div>
                  <div className="font-medium">Fast shipping</div>
                  <div className="text-[11px] text-gray-500">
                    5–10 business days across Canada & US
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-lg">🔒</span>
                <div>
                  <div className="font-medium">Secure checkout</div>
                  <div className="text-[11px] text-gray-500">
                    Powered by Stripe, PCI compliant
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-lg">✅</span>
                <div>
                  <div className="font-medium">Quality guaranteed</div>
                  <div className="text-[11px] text-gray-500">
                    Warranty & dedicated support
                  </div>
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div className="border-t border-gray-200 pt-4">
              <div className="flex gap-6 border-b border-gray-100">
                {["description", "specs", "shipping"].map((tab) => (
                  <button
                    key={tab}
                    type="button"
                    onClick={() => setActiveTab(tab)}
                    className={`pb-3 text-sm font-medium -mb-px border-b-2 transition ${
                      activeTab === tab
                        ? "border-black text-black"
                        : "border-transparent text-gray-500 hover:text-black"
                    }`}
                  >
                    {tab.charAt(0).toUpperCase() + tab.slice(1)}
                  </button>
                ))}
              </div>

              <div className="pt-4 text-sm text-gray-600 leading-relaxed">
                {activeTab === "description" && (
                  <p>
                    {product.description ||
                      "Details for this product will appear here."}
                  </p>
                )}

                {activeTab === "specs" && (
                  <>
                    {product.specs &&
                    Object.keys(product.specs).length > 0 ? (
                      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 text-sm">
                        {Object.entries(product.specs).map(
                          ([key, value]) => (
                            <div
                              key={key}
                              className="flex justify-between gap-4"
                            >
                              <dt className="text-gray-500">
                                {key.replace(/_/g, " ")}
                              </dt>
                              <dd className="text-gray-900 text-right">
                                {String(value)}
                              </dd>
                            </div>
                          )
                        )}
                      </dl>
                    ) : (
                      <p className="text-gray-500">
                        No specifications added yet.
                      </p>
                    )}
                  </>
                )}

                {activeTab === "shipping" && (
                  <div>
                    <p className="mb-2">
                      🚚 <strong>Shipping:</strong> Typically ships within
                      5–10 business days.
                    </p>
                    <p className="mb-2">
                      📦 Your product is carefully packaged and shipped with
                      full tracking.
                    </p>
                    <p className="text-xs text-gray-500">
                      For bulk orders or special delivery needs, please contact
                      our support team.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Related products */}
        <section className="mt-16">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900">
              You may also like
            </h2>
            <Link
              to="/products"
              className="text-sm text-blue-600 hover:underline"
            >
              View all products
            </Link>
          </div>

          {relatedProducts.length === 0 ? (
            <p className="text-sm text-gray-500">
              No related products found yet.
            </p>
          ) : (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {relatedProducts.map((rp) => (
                <Link
                  key={rp._id}
                  to={`/products/${rp._id}`}
                  className="bg-white border border-gray-100 rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition flex flex-col"
                >
                  <div className="aspect-[4/3] bg-gray-50">
                    {rp.images?.[0] ? (
                      <img
                        src={rp.images[0]}
                        alt={rp.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-xs text-gray-400">
                        No image
                      </div>
                    )}
                  </div>
                  <div className="p-4 flex-1 flex flex-col">
                    <div className="text-xs uppercase text-gray-400 mb-1">
                      {rp.category}
                    </div>
                    <div className="font-medium text-sm mb-1 line-clamp-2">
                      {rp.name}
                    </div>
                    <div className="mt-auto text-sm font-semibold text-gray-900">
                      ${rp.price?.toLocaleString()}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </section>

        {/* Back link */}
        <div className="mt-10 mb-4">
          <Link
            to="/products"
            className="text-sm text-blue-600 hover:underline"
          >
            ← Back to all products
          </Link>
        </div>
      </div>
    </div>
  );
}
